import { getCurrentUser } from "../auth/service";
import axiosInstance from "../axios";

export const getProjectSettings = async (projectId) => {
  const currentUser = getCurrentUser();

  if (!currentUser) {
    throw Error("Please Login to see your projects", { cause: { code: 403 } });
  }

  return axiosInstance.get(
    `/users/${currentUser.id}/projects/${projectId}/project_settings`
  );
};

export const removeOverrides = ({ projectId, propertyIdentifier }) => {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw Error("Please Login to see your projects", { cause: { code: 403 } });
  }

  return axiosInstance.patch(
    `/users/${currentUser.id}/projects/${projectId}/remove_overrides`,
    {
      property_identifier: propertyIdentifier,
    }
  );
};

export const updateServiceTier = ({ projectId, serviceTierId }) => {
  return axiosInstance.put(`projects/${projectId}/update_factory_tier`, {
    factory_tier_id: serviceTierId,
  });
};

export const getPricingSummary = ({ projectId, orderId }) => {
  return axiosInstance.get(`orders/${orderId}/pricing_summary`);
};

export const getLabelPrinting = ({ projectId, orderId }) => {
  return axiosInstance.get(`orders/${orderId}/panels_for_printing`);
};

export const getOrderCuttingPlan = async ({
  projectId,
  orderId,
  forceRefresh,
}) => {
  const response = await axiosInstance.get(
    `/orders/${orderId}/cutting_plan?force_refresh=${forceRefresh}`,
    {
      id: projectId,
      force_refresh: forceRefresh,
    }
  );

  return response;
};

export const getOrderCutlist = ({ projectId, orderId }) => {
  return axiosInstance.get(`orders/${orderId}/cutlist.json`);
};

export const getSignedCutlistURL = ({ projectId, orderId, cutlistFormat }) => {
  return axiosInstance.post(`orders/${orderId}/signed_cutlist_url.json`, {
    cl_format: cutlistFormat,
  });
};

export const getSignedPricingExportURL = ({ projectId, orderId }) => {
  return axiosInstance.post(`orders/${orderId}/signed_pricing_export_url.json`);
};

export const getSignedDrillingExportURL = ({ projectId }) => {
  return axiosInstance.get(
    `projects/${projectId}/signed_drilling_export_url.json`
  );
};

export const getAssembly = ({ projectId }) => {
  return axiosInstance.get(`projects/${projectId}/assembly`);
};

export const materialOptions = ({ projectId, category }) => {
  return axiosInstance.get(
    `projects/${projectId}/material_options?material_category=${category}`
  );
};

export const updateMaterialForOption = ({
  projectId,
  optionIdentifier,
  materialIdentifier,
  materialCategory,
}) => {
  return axiosInstance.put(`projects/${projectId}/update_material_for_option`, {
    option_identifier: optionIdentifier,
    material_identifier: materialIdentifier,
    material_category: materialCategory,
  });
};

export const updateEdgeMaterial = ({
  projectId,
  edgeThickness,
  edgeLaminateIdentifier,
  edgePanelThickness,
  edgeMaterialIdentifier,
}) => {
  return axiosInstance.put(`projects/${projectId}/update_edge_material`, {
    edge_thickness: edgeThickness,
    edge_laminate_identifier: edgeLaminateIdentifier,
    edge_panel_thickness: edgePanelThickness,
    edge_material_identifier: edgeMaterialIdentifier,
  });
};

export const createEdgeMaterial = ({ brand, name, code, thickness, width }) => {
  return axiosInstance.post(`edge_materials`, {
    brand: brand,
    name: name,
    code: code,
    thickness: thickness,
    width: width,
  });
};

export const getBomSummary = (projectId, refresh = false) => {
  return axiosInstance.post(`/projects/${projectId}/bom_summary`, {
    refresh: refresh,
  });
};

export const markBomSummaryStale = (bomSummary, setBomSummary) => {
  setBomSummary({ ...bomSummary, is_stale: true });
};

export const getCuttingGroupCutlist = ({
  cuttingGroupIdentifier,
  projectId,
  acceptHeader,
  orderId,
}) => {
  return axiosInstance.get(
    `orders/${orderId}/cutting_group_cutlist?cutting_group_identifier=${cuttingGroupIdentifier}`,
    {
      headers: {
        Accept: acceptHeader,
      },
    }
  );
};

export const getCuttingGroupCutlistCsv = ({
  cuttingGroupIdentifier,
  projectId,
  orderId,
  acceptHeader,
  responseType,
  downloadFormat,
}) => {
  return axiosInstance.get(
    `orders/${orderId}/cutting_group_cutlist?cutting_group_identifier=${cuttingGroupIdentifier}&download_format=${downloadFormat}`,
    {
      headers: {
        Accept: acceptHeader,
      },
      responseType: responseType,
    }
  );
};

export const splitOrder = ({ projectId }) => {
  return axiosInstance.put(`projects/${projectId}/split_order`);
};

export const mergeOrders = ({ projectId }) => {
  return axiosInstance.put(`projects/${projectId}/merge_orders`);
};

export const updateOrderStatus = ({ projectId, orderId, status }) => {
  return axiosInstance.patch(`orders/${orderId}/update_status`, {
    status: status,
  });
};

export const getMaterials = ({ projectId, orderId }) => {
  return axiosInstance.get(`orders/${orderId}/get_materials`);
};

export const getServices = ({ projectId, orderId }) => {
  return axiosInstance.get(`orders/${orderId}/get_services`);
};

export const createPriceLineItem = ({
  materialId,
  newQuantity,
  price,
  remarks,
  orderId,
}) => {
  return axiosInstance.post(`orders/${orderId}/create_price_line_item`, {
    material_id: materialId,
    new_quantity: newQuantity,
    price,
    remarks,
  });
};

export const createServicePricingItem = ({
  factoryServiceId,
  newQuantity,
  price,
  remarks,
  orderId,
}) => {
  return axiosInstance.post(`orders/${orderId}/create_service_pricing_item`, {
    factory_service_id: factoryServiceId,
    new_quantity: newQuantity,
    price,
    remarks,
  });
};

export const updatePriceLineItem = ({
  orderId,
  priceLineItemId,
  newQuantity,
  price,
  newRemarks,
}) => {
  return axiosInstance.patch(`orders/${orderId}/update_price_line_item`, {
    price_line_item_id: priceLineItemId,
    new_quantity: newQuantity,
    price,
    new_remarks: newRemarks,
  });
};

export const deletePriceLineItem = ({
  projectId,
  orderId,
  priceLineItemId,
}) => {
  return axiosInstance.delete(`orders/${orderId}/delete_price_line_item`, {
    params: { price_line_item_id: priceLineItemId },
  });
};

export const updateServicePricingItem = ({
  orderId,
  servicePricingItemId,
  newQuantity,
  price,
  newRemarks,
}) => {
  return axiosInstance.patch(`orders/${orderId}/update_service_pricing_item`, {
    service_pricing_item_id: servicePricingItemId,
    new_quantity: newQuantity,
    price,
    new_remarks: newRemarks,
  });
};

export const deleteServicePricingItem = ({
  projectId,
  orderId,
  servicePricingItemId,
}) => {
  return axiosInstance.delete(`orders/${orderId}/delete_service_pricing_item`, {
    params: { service_pricing_item_id: servicePricingItemId },
  });
};

export const updateDiscount = ({
  projectId,
  orderId,
  newDiscount,
  newDiscountRemarks,
}) => {
  return axiosInstance.patch(`orders/${orderId}/update_discount`, {
    order_id: orderId,
    discount: newDiscount,
    remarks: newDiscountRemarks,
  });
};

export const clearDiscountAndRemarks = ({ projectId, orderId }) => {
  return axiosInstance.patch(`orders/${orderId}/clear_discount_and_remarks`);
};

export const refreshLineItemPrices = ({ projectId, orderId }) => {
  return axiosInstance.patch(`orders/${orderId}/refresh_line_item_prices`);
};

export const getDeliveryDate = ({ projectId, orderId }) => {
  return axiosInstance.get(`orders/${orderId}/estimated_delivery_date`);
};

export const getProjectsForUser = async ({ org_id, status }) => {
  return axiosInstance.get(`/projects?org_id=${org_id}&status=${status}`);
};

export const getProject = async ({ projectId }) => {
  const currentUser = getCurrentUser();

  if (!currentUser) {
    throw Error("Please Login to see your projects", { cause: { code: 403 } });
  }

  return axiosInstance.get(
    `/users/${currentUser.id}/projects/${projectId}?version=${2}`
  );
};

export const getProjectSummary = async (projectId) => {
  return axiosInstance.get(`/projects/${projectId}/summary`);
};

export const updateUserProject = async ({ projectId, payload }) => {
  const currentUser = getCurrentUser();

  if (!currentUser) {
    throw Error("Please Login to see your projects", { cause: { code: 403 } });
  }

  return axiosInstance.patch(`/users/${currentUser.id}/projects/${projectId}`, {
    ...payload,
  });
};

export const deleteUserProject = async (projectId) => {
  const currentUser = getCurrentUser();

  if (!currentUser) {
    throw Error("Please Login to see your projects", { cause: { code: 403 } });
  }

  return axiosInstance.delete(`/users/${currentUser.id}/projects/${projectId}`);
};

export const getRoom = async ({ projectId, roomId }) => {
  return axiosInstance.get(`/projects/${projectId}/rooms/${parseInt(roomId)}`);
};

export const addUserRoom = async ({ projectId, payload }) => {
  return axiosInstance.put(`/projects/${projectId}/add_room`, {
    ...payload,
  });
};

export const updateUserRoom = async ({ projectId, roomId, payload }) => {
  return axiosInstance.patch(`/projects/${projectId}/rooms/${roomId}`, {
    ...payload,
  });
};

export const deleteUserRoom = async ({ projectId, roomId }) => {
  return axiosInstance.delete(`/projects/${projectId}/rooms/${roomId}`);
};

export const addUserGroup = async ({ projectId, roomId, payload }) => {
  return axiosInstance.put(`/projects/${projectId}/rooms/${roomId}/add_group`, {
    ...payload,
  });
};

export const updateUserGroup = async ({ projectId, groupId, payload }) => {
  return axiosInstance.patch(`/projects/${projectId}/groups/${groupId}`, {
    ...payload,
  });
};

export const deleteUserGroup = async ({ projectId, groupId }) => {
  return axiosInstance.delete(`/projects/${projectId}/groups/${groupId}`);
};

export const updateOrderItemProperties = async ({
  projectId,
  orderItemIds,
  properties,
  dimensions,
  carcassExtensions,
}) => {
  const payload = { properties, dimensions };

  if (carcassExtensions && !_.isEmpty(carcassExtensions)) {
    payload.carcass_extensions = { ...carcassExtensions };
  }
  if (orderItemIds && orderItemIds.length) {
    Object.assign(payload, { order_item_ids: orderItemIds.map(Number) });
  }

  return axiosInstance.put(
    `/projects/${projectId}/update_order_items`,
    payload
  );
};

export const copyPasteOrderItem = ({
  projectId,
  orderItemId,
  copy_id,
  column,
}) => {
  return axiosInstance.post(
    `/projects/${projectId}/groups/${null}/order_items/${orderItemId}/copy_paste`,
    {
      copy_id,
      column,
    }
  );
};

export const copyPasteSection = ({ id, copy_id, column }) => {
  return axiosInstance.post(`/sections/${id}/copy_paste_section`, {
    copy_id,
    column,
  });
};

export const createUserProject = async ({ name, version }) => {
  const currentUser = getCurrentUser();

  return axiosInstance.post(`/users/${currentUser.id}/projects/`, {
    project_info: { name, app_version: version },
  });
};

export const getOrder = async ({ projectId, orderId }) => {
  return axiosInstance.get(`/orders/${orderId}`);
};

export const placeOrder = ({ projectId, place_multiple_orders }) => {
  return axiosInstance.post(`/projects/${projectId}/place_order`, {
    place_multiple_orders: place_multiple_orders,
  });
};

export const updateProjectSettings = async ({
  projectId,
  identifier,
  value,
}) => {
  return axiosInstance.put(`/projects/${projectId}/update_project_settings`, {
    identifier,
    value,
  });
};

export const getContainers = async () => {
  return axiosInstance.get(`/containers`);
};

export const sortContainerOrderItems = async ({
  projectId,
  orderItemId,
  payload,
  groupId,
}) => {
  return axiosInstance.put(
    `/projects/${projectId}/groups/${groupId}/order_items/${orderItemId}/sort_container_items`,
    {
      payload,
    }
  );
};

export const sortGroupOrderItems = async ({ projectId, groupId, payload }) => {
  return axiosInstance.put(
    `/projects/${projectId}/groups/${groupId}/sort_items`,
    {
      payload,
    }
  );
};

export const sortSectionItems = async ({ projectId, groupId, payload }) => {
  return axiosInstance.put(
    `/projects/${projectId}/groups/${groupId}/update_item_sort_position`,
    {
      payload,
    }
  );
};

export const sortDirectItems = async ({ projectId, roomId, payload }) => {
  return axiosInstance.put(
    `/projects/${projectId}/rooms/${roomId}/update_room_sort_order`,
    {
      payload,
    }
  );
};

export const packModules = async ({ projectId, groupId, payload }) => {
  return axiosInstance.put(
    `/projects/${projectId}/groups/${groupId}/pack_modules`,
    {
      payload,
    }
  );
};

export const getGroup = async ({ projectId, groupId }) => {
  return axiosInstance.get(`/projects/${projectId}/groups/${groupId}`);
};

export const duplicateOrderItem = async ({
  projectId,
  groupId,
  orderItemId,
  count,
}) => {
  return axiosInstance.post(
    `/projects/${projectId}/groups/${groupId}/duplicate_item`,
    {
      order_item_id: orderItemId,
      count: count,
    }
  );
};

export const groupItems = async ({ projectId, roomId, payload }) => {
  return axiosInstance.put(
    `/projects/${projectId}/rooms/${roomId}/group_items`,
    {
      ...payload,
    }
  );
};

export const unGroupItems = async ({ projectId, roomId, groupIds }) => {
  return axiosInstance.put(
    `/projects/${projectId}/rooms/${roomId}/ungroup_items`,
    {
      group_ids: groupIds,
    }
  );
};

export const cloneItems = async ({ projectId, roomId, items }) => {
  return axiosInstance.put(
    `/projects/${projectId}/rooms/${roomId}/clone_items`,
    {
      items,
    }
  );
};

export const cloneProject = async ({ projectId, project }) => {
  return axiosInstance.put(`/projects/${projectId}/clone`, {
    project,
  });
};

export const cloneRoom = async ({ projectId, roomId }) => {
  return axiosInstance.put(`/projects/${projectId}/rooms/${roomId}/clone`);
};

export const addLaminate = ({ file, details }) => {
  const formData = new FormData();

  for (const key in details) formData.append(key, details[key]);

  formData.append("file", file);

  return axiosInstance.post(`/laminates`, formData);
};

export const mappedColors = async ({ projectId }) => {
  return axiosInstance.get(`/projects/${projectId}/mapped_colors`);
};

export const getLaminates = async ({ searchTerm, manage, status }) => {
  return axiosInstance.get(
    `/laminates?search_term=${searchTerm}&manage=${manage}&status=${status}`
  );
};

export const getAddOns = async ({ projectId }) => {
  return axiosInstance.get(`/projects/${projectId}/add_ons`);
};

export const createAddOn = async ({ projectId, item, price }) => {
  return axiosInstance.post(`/projects/${projectId}/add_ons`, {
    item,
    price,
  });
};

export const updateAddOn = async ({ projectId, id, item, price }) => {
  return axiosInstance.put(`/projects/${projectId}/add_ons/${id}`, {
    item,
    price,
  });
};

export const deleteAddOn = async ({ projectId, id }) => {
  return axiosInstance.delete(`/projects/${projectId}/add_ons/${id}`);
};

export const getSignedToken = async ({ projectId, resource }) => {
  const currentUser = getCurrentUser();

  if (!currentUser) throw Error("Please Login", { cause: { code: 403 } });

  return axiosInstance.post(`/users/${currentUser.id}/signed_token`, {
    project_id: projectId,
    resource,
  });
};

export const importProject = ({ file, canClearProject, projectId }) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("can_clear_project", canClearProject);

  return axiosInstance.post(`/projects/${projectId}/import_project`, formData);
};

export const updateLaminate = ({
  id,
  brand,
  range,
  code,
  product_name,
  finish,
  thickness,
  category,
  rotatable,
  image,
}) => {
  const formData = new FormData();
  if (brand) formData.append("brand", brand);
  if (range) formData.append("range", range);
  if (code) formData.append("code", code);
  if (product_name) formData.append("product_name", product_name);
  if (finish) formData.append("finish", finish);
  if (thickness) formData.append("thickness", thickness);
  if (category) formData.append("category", category);
  formData.append("rotatable", rotatable);
  if (image) formData.append("image", image);

  return axiosInstance.patch(`/laminates/${id}`, formData);
};

export const publishLaminate = async (id) => {
  return axiosInstance.post(`/laminates/${id}/publish`);
};

export const approveLaminate = async (id) => {
  return axiosInstance.put(`/laminates/${id}/approve_price`);
};

export const getOrderInfo = ({ projectId }) => {
  return axiosInstance.get(`projects/${projectId}/order_info`);
};

export const refreshPrice = (projectId, force = false) => {
  return axiosInstance.post(`/projects/${projectId}/refresh_price`, {
    force: force,
  });
};

export const refreshTemplates = (projectId) => {
  return axiosInstance.post(`/projects/${projectId}/refresh_templates`);
};

export const deleteColor = ({ projectId, optionIdentifier }) => {
  return axiosInstance.patch(`projects/${projectId}/delete_color`, {
    option_identifier: optionIdentifier,
  });
};

export const updateSection = ({ id, dimension, sides, property, name }) => {
  return axiosInstance.put(`/sections/${id}`, {
    dimension,
    sides,
    property,
    name,
  });
};

export const addItemToSection = ({ id, productIds, temp_ids }) => {
  return axiosInstance.post(`/sections/${id}/add_items`, {
    product_ids: productIds,
    temp_ids: temp_ids,
  });
};

export const createSection = (sectionData, projectId, roomId) => {
  return axiosInstance.post(
    `/projects/${projectId}/rooms/${roomId}/create_section`,
    sectionData
  );
};

export const loadGroup = async ({ projectId, roomId, groupId }) => {
  return axiosInstance.get(`/sections/${groupId}`);
};

export const loadModule = async ({ projectId, roomId, groupId, moduleId }) => {
  return axiosInstance.get(
    `/projects/${projectId}/groups/${groupId}/order_items/${moduleId}`
  );
};

export const updateRealOrder = (projectId, value) => {
  return axiosInstance.post(`/projects/${projectId}/update_real_order`, {
    value: value,
  });
};
